import React, { useState } from 'react';
import { Platform } from 'react-native';
import { CURRENT_USER } from '@ventureroom/core';
import { useApp, useData, useMe } from './provider';
import {
  Avatar,
  Button,
  Card,
  Column,
  Copy,
  Empty,
  FormSheet,
  Heading,
  Page,
  Row,
  Sheet,
  Tag,
  dateLabel,
} from './components';
export function Activity() {
  const data = useData();
  const { navigate, act } = useApp();
  const invitations = data.invitations.filter(
    (i) => i.to === CURRENT_USER && i.status === 'pending',
  );
  const items = data.activities
    .filter(
      (a) =>
        !a.ventureId ||
        data.ventures.some((v) => v.id === a.ventureId && v.members.includes(CURRENT_USER)),
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return (
    <Page title="Activity">
      {invitations.map((i) => (
        <Card key={i.id}>
          <Tag>{i.kind === 'request' ? 'Join request' : 'Venture invitation'}</Tag>
          <Heading size={20}>{data.ventures.find((v) => v.id === i.ventureId)?.name}</Heading>
          <Copy>
            {data.profiles.find((p) => p.id === i.from)?.name}{' '}
            {i.kind === 'request' ? 'would like to join your team.' : 'invited you to the team.'}
          </Copy>
          <Row>
            <Button
              onPress={() =>
                void act(
                  { type: 'invitationRespond', id: i.id, accept: true },
                  'Venture invitation accepted',
                )
              }
            >
              Accept
            </Button>
            <Button
              kind="quiet"
              onPress={() =>
                void act(
                  { type: 'invitationRespond', id: i.id, accept: false },
                  'Invitation declined',
                )
              }
            >
              Decline
            </Button>
          </Row>
        </Card>
      ))}
      {items.length ? (
        items.map((a) => (
          <Card
            key={a.id}
            onPress={() =>
              navigate(
                a.ventureId
                  ? `venture/${a.ventureId}`
                  : a.kind === 'connection'
                    ? 'connections'
                    : 'profile',
              )
            }
          >
            <Row between>
              <Tag>{a.kind}</Tag>
              <Copy muted size={12}>
                {dateLabel(a.createdAt)}
              </Copy>
            </Row>
            <Heading size={19}>{a.title}</Heading>
            <Copy muted size={13}>
              {a.detail}
            </Copy>
          </Card>
        ))
      ) : (
        <Empty
          title="Your next chapter starts here"
          detail="Connections, decisions, and team progress will appear in this timeline."
        />
      )}
    </Page>
  );
}
export function Settings() {
  const app = useApp();
  const me = useMe();
  const [pair, setPair] = useState(false),
    [confirmReset, setConfirmReset] = useState(false),
    [confirmDelete, setConfirmDelete] = useState(false),
    [info, setInfo] = useState<string | null>(null);
  return (
    <Page title="Settings" back="profile">
      <Card>
        <Heading size={20}>Appearance</Heading>
        <Row wrap>
          <Button
            kind={app.theme === 'warm' ? 'primary' : 'secondary'}
            onPress={() => void app.setTheme('warm')}
          >
            ☀ Light Mode
          </Button>
          <Button
            kind={app.theme === 'dark' ? 'primary' : 'secondary'}
            onPress={() => void app.setTheme('dark')}
          >
            ◐ Dark Mode
          </Button>
        </Row>
      </Card>
      <Card>
        <Heading size={20}>Accessibility</Heading>
        <Row between wrap>
          <Column gap={4}>
            <Copy bold>Reduce motion & haptics</Copy>
            <Copy muted size={13}>
              Keep transitions and feedback quiet.
            </Copy>
          </Column>
          <Button kind="secondary" onPress={() => void app.setReduceMotion(!app.reduceMotion)}>
            {app.reduceMotion ? 'On' : 'Off'}
          </Button>
        </Row>
      </Card>
      <Card>
        <Heading size={20}>Collaboration preferences</Heading>
        <Row between wrap>
          <Copy>Open to new collaborators</Copy>
          <Button
            kind="secondary"
            onPress={() =>
              void app.act(
                { type: 'profile', profile: { ...me, looking: !me.looking } },
                'Discovery preference updated',
              )
            }
          >
            {me.looking ? 'Open' : 'Taking a pause'}
          </Button>
        </Row>
      </Card>
      <Card>
        <Row between>
          <Heading size={20}>Local development</Heading>
          <Tag>{app.mode === 'server' ? 'VM connected' : 'Offline workspace'}</Tag>
        </Row>
        <Copy muted>
          Offline mode stays on this device. Connecting to the VM opens its shared workspace. The
          two datasets stay separate; switching does not upload or overwrite your offline work.
        </Copy>
        <Row wrap>
          {app.mode === 'server' ? (
            <>
              <Button kind="secondary" onPress={() => void app.refresh()}>
                Refresh from backend
              </Button>
              <Button kind="quiet" onPress={() => void app.disconnect()}>
                Use offline workspace
              </Button>
            </>
          ) : (
            <Button kind="secondary" onPress={() => setPair(true)}>
              Connect local backend
            </Button>
          )}
          <Button
            kind="quiet"
            disabled={app.mode === 'server'}
            onPress={() => setConfirmReset(true)}
          >
            Reset demo data
          </Button>
        </Row>
      </Card>
      <Card>
        <Heading size={20}>Account management</Heading>
        <Row between wrap>
          <Column gap={4}>
            <Copy bold>Delete account</Copy>
            <Copy muted size={13}>Permanently remove your profile and data.</Copy>
          </Column>
          <Button kind="danger" onPress={() => setConfirmDelete(true)}>
            Delete account
          </Button>
        </Row>
      </Card>
      <Card>
        <Heading size={20}>Good to know</Heading>
        {['Privacy & storage', 'Notifications', 'Account', 'About VentureRoom'].map((label) => (
          <Row between key={label}>
            <Copy>{label}</Copy>
            <Button label={`Open ${label}`} kind="quiet" onPress={() => setInfo(label)}>
              Read →
            </Button>
          </Row>
        ))}
      </Card>
      {pair && (
        <FormSheet
          title="Connect your local backend"
          fields={[
            { key: 'url', label: 'Backend URL', required: true },
            {
              key: 'token',
              label: 'Local pairing token',
              type: 'secret',
              hint: 'Stored securely on mobile; in this browser session on web.',
              required: true,
            },
          ]}
          initial={{
            url: Platform.OS === 'android' ? 'http://10.0.2.2:8787' : 'http://127.0.0.1:8787',
            token: '',
          }}
          onClose={() => setPair(false)}
          submit="Connect"
          onSave={(v) => app.pair(String(v.url), String(v.token))}
        />
      )}
      {confirmReset && (
        <Sheet title="Start the demo afresh?" onClose={() => setConfirmReset(false)}>
          <Copy>
            This replaces this device’s offline founder profiles, messages, and venture records with
            fictional examples. The backend workspace is unaffected.
          </Copy>
          <Button
            kind="danger"
            onPress={() => {
              void app.reset();
              setConfirmReset(false);
            }}
          >
            Reset offline demo
          </Button>
          <Button kind="quiet" onPress={() => setConfirmReset(false)}>
            Keep my changes
          </Button>
        </Sheet>
      )}
      {confirmDelete && (
        <Sheet title="Delete your account?" onClose={() => setConfirmDelete(false)}>
          <Copy>
            This will permanently remove your profile, ventures, and all associated data. This action cannot be undone.
          </Copy>
          <Button
            kind="danger"
            onPress={() => {
              void app.reset();
              setConfirmDelete(false);
            }}
          >
            Yes, delete account
          </Button>
          <Button kind="quiet" onPress={() => setConfirmDelete(false)}>
            Cancel
          </Button>
        </Sheet>
      )}
      {info && (
        <Sheet title={info} onClose={() => setInfo(null)}>
          {info === 'Privacy & storage' ? (
            <Copy>
              Offline records stay in this device’s local database. In VM mode, records are sent to
              your local backend. There are no analytics, external photos, or tracking services.
              Demo reset erases offline changes. Browser storage can also be cleared through your
              browser settings. A public release will need account-level access controls,
              moderation, and a privacy policy.
            </Copy>
          ) : info === 'Notifications' ? (
            <Copy>
              Your Activity tab brings together invitations and team progress. This beta does not
              send push notifications or emails. Open the app to catch up; calendar integration can
              be added later.
            </Copy>
          ) : info === 'Account' ? (
            <Column>
              <Avatar person={me} />
              <Heading size={20}>{me.name}</Heading>
              <Copy>
                This beta uses one local founder identity. No password, email address, or external
                account is required. Connected devices paired to the development VM share that
                identity.
              </Copy>
            </Column>
          ) : (
            <Column>
              <Heading size={24}>VentureRoom</Heading>
              <Copy>
                Version 0.1.0 · A place for complementary people to build businesses together.
              </Copy>
              <Copy muted>
                Founder matching and a lightweight team workspace. No payments, investment listings,
                wallets, or financial return calculations.
              </Copy>
              <Copy muted size={12}>
                Temporary original brand assets. All seeded people are fictional.
              </Copy>
            </Column>
          )}
        </Sheet>
      )}
      <Card>
        <Heading size={20}>Developer Settings</Heading>
        <Row between wrap>
          <Column gap={4}>
            <Copy bold>OpenAI API Key</Copy>
            <Copy muted size={13}>
              Used for local AI responses. Not saved to server.
            </Copy>
          </Column>
        </Row>
        <input
          type="password"
          value={app.openaiKey || ''}
          onChange={(e) => void app.setOpenaiKey(e.target.value)}
          placeholder="sk-proj-..."
          style={{
            width: '100%',
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            marginTop: '8px',
            backgroundColor: 'transparent',
            color: 'inherit'
          }}
        />
      </Card>
      <SubscriptionManager />
      <Card>
        <Heading size={20}>Top Up Credits</Heading>
        <Copy muted size={13}>
          VentureRoom charges a 20% platform fee. The remaining 80% is converted into AI generation words.
        </Copy>
        <Column gap={12} style={{ marginTop: 12 }}>
          <TopUpCredits />
        </Column>
      </Card>
    </Page>
  );
}


function SubscriptionManager() {
  const app = useApp();
  const me = useMe();
  const role = me.role || 'Business Owner';
  
    const founderPlans = [
    { id: 'free', name: 'Free', price: '$0/mo', features: ['1 Venture maximum', 'Basic AI chat'] },
    { id: 'starter', name: 'Starter', price: '$49/mo', features: ['Up to 5 Ventures', '15,000 AI words/mo', 'Priority support'] },
    { id: 'pro', name: 'Pro', price: '$99/mo', features: ['Up to 10 Ventures', 'Website Stats Dashboard', '30,000 AI words/mo', 'Team Digest', 'Priority support'] },
    { id: 'enterprise', name: 'Enterprise', price: '$299/mo', features: ['Unlimited Ventures', 'Website Stats Dashboard', '50,000 AI words/mo', 'Team Digest & Priority support', 'Document Uploads', 'The AI Boardroom'] }
  ];

      const freelancerPlans = [
    { id: 'free', name: 'Free', price: '$0/mo', features: ['1 active project'] },
    { id: 'pro', name: 'Pro', price: '$29/mo', features: ['Up to 2 active projects', '7,000 AI words/mo', 'Basic Analytics', 'Minor Priority Matching'] },
    { id: 'elite', name: 'Elite', price: '$49/mo', features: ['Unlimited projects', '15,000 AI words/mo', 'Advanced Analytics', 'Major Priority Matching'] },
    { id: 'agency', name: 'Agency', price: '$149/mo', features: ['Unlimited projects', '30,000 AI words/mo', 'Advanced Analytics', 'Max Visibility', 'Verified Badge', 'Ability to create your own team'] }
  ];

  const plans = role === 'Skill Provider' ? freelancerPlans : founderPlans;
  const currentTier = me.subscriptionTier || 'free';

  return (
    <Card>
      <Heading size={20}>Subscription Plan</Heading>
      <Copy muted size={13}>Manage your VentureRoom subscription.</Copy>
      <Column gap={12} style={{ marginTop: 16 }}>
        {plans.map(plan => (
          <div key={plan.id} style={{ 
            padding: 16, 
            border: currentTier === plan.id ? '2px solid #007bff' : '1px solid #ddd', 
            borderRadius: 8,
            backgroundColor: currentTier === plan.id ? 'rgba(0, 123, 255, 0.05)' : 'transparent'
          }}>
            <Row between style={{ alignItems: "center" }}>
              <Column>
                <Heading size={16}>{plan.name} <span style={{fontWeight:'normal', color:'#666'}}>{plan.price}</span></Heading>
                <Copy size={13} muted>{plan.features.join(' - ')}</Copy>
              </Column>
              <Button 
                kind={currentTier === plan.id ? 'primary' : 'secondary'}
                disabled={currentTier === plan.id}
                onPress={() => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  app.act({ type: 'subscribe', tier: plan.id } as any, `Upgraded to ${plan.name}!`);
                }}
              >
                {currentTier === plan.id ? 'Current Plan' : 'Select'}
              </Button>
            </Row>
          </div>
        ))}
      </Column>
    </Card>
  );
}

function TopUpCredits() {
  const data = useData();
  const app = useApp();
  const [amount, React_useState_amount] = React.useState('10');
  const [ventureId, React_useState_ventureId] = React.useState('');
  const [isShared, React_useState_isShared] = React.useState(false);

  const val = parseFloat(amount) || 0;
  const platformFee = val * 0.20;
  const aiAllocation = val * 0.80;
  const estimatedWords = Math.floor(aiAllocation * 31250);

  const ventures = data.ventures.filter(v => v.members.includes(CURRENT_USER));

  if (!ventures.length) return <Copy muted>You need to be in a venture to buy credits.</Copy>;

  return (
    <Column gap={12}>
      <Copy bold size={14}>Select Venture</Copy>
      <select value={ventureId} onChange={e => React_useState_ventureId(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc' }}>
        <option value="">-- Choose a venture --</option>
        {ventures.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
      </select>

      <Copy bold size={14}>Amount - Min $5</Copy>
      <input type="text" value={amount} onChange={e => React_useState_amount(e.target.value)} placeholder="e.g. 10" style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />

      <div style={{ backgroundColor: '#f1f5f9', padding: 12, borderRadius: 8 }}>
        <Copy size={13} style={{ color: '#0f172a' }}>Platform Fee (20%): ${platformFee.toFixed(2)}</Copy>
        <Copy size={13} style={{ color: '#0f172a' }}>AI Allocation (80%): ${aiAllocation.toFixed(2)}</Copy>
        <Copy size={13} bold style={{ color: '#0f172a' }}>Estimated Words: {estimatedWords.toLocaleString()}</Copy>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input type="checkbox" checked={isShared} onChange={e => React_useState_isShared(e.target.checked)} />
        <Copy size={13}>Allow other people on the team to use these credits</Copy>
      </label>

      <Button 
        disabled={val < 5 || !ventureId} 
        onPress={() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          app.act({ type: 'topUpCredits', ventureId, amount: val, isShared } as any, 'Credits successfully topped up!');
          React_useState_amount('');
          React_useState_ventureId('');
        }}
      >
        Buy Credits
      </Button>
    </Column>
  );
}
